import { useEffect, useState, useCallback } from "react";
import { Search, X, Plus, Mail, Phone, Globe, MapPin, UserPlus, Trash2, FolderOpen, Download, FileText, Printer, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["Prospect", "Active", "Former"];
const badge = (s) => ({ Prospect: "bg-amber-100 text-amber-700", Active: "bg-emerald-100 text-emerald-700", Former: "bg-slate-200 text-slate-600" }[s] || "bg-slate-100 text-slate-600");
const fmt = (d) => { try { return new Date(d).toLocaleString("en-GB"); } catch { return d; } };
const EMPTY = { first_name: "", last_name: "", email: "", phone: "", nationality: "", dob: "", address: "", city: "", postcode: "", country: "", status: "Prospect", source: "manual", comm_preference: "Email", whatsapp: "" };
const COMM_PREFS = ["Email", "WhatsApp", "Both"];

export default function ClientsManager() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState({});
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [contact, setContact] = useState({ name: "", relationship: "", email: "", phone: "" });
  const curFy = new Date().getFullYear();
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmt, setStmt] = useState({ fy: String(curFy), date_from: "", date_to: "" });
  const [stmtData, setStmtData] = useState(null);
  const [stmtSending, setStmtSending] = useState(false);
  const [stmtEmail, setStmtEmail] = useState("");

  const stmtParams = useCallback(() => {
    const p = {};
    if (stmt.date_from || stmt.date_to) { if (stmt.date_from) p.date_from = stmt.date_from; if (stmt.date_to) p.date_to = stmt.date_to; }
    else if (stmt.fy !== "all") p.financial_year = stmt.fy;
    return p;
  }, [stmt]);
  const loadStmt = useCallback(() => {
    if (!active) return;
    api.get(`/admin/clients/${active.id}/statement/data`, { params: stmtParams() }).then((r) => setStmtData(r.data)).catch(() => setStmtData(null));
  }, [active, stmtParams]);
  useEffect(() => { if (stmtOpen) loadStmt(); }, [stmtOpen, loadStmt]);
  const openStatement = () => { setStmtEmail(active.email || ""); setStmtOpen(true); };
  const stmtPdf = async (print = false) => {
    try {
      const r = await api.get(`/admin/clients/${active.id}/statement/pdf`, { params: stmtParams(), responseType: "blob" });
      const w = window.open(URL.createObjectURL(r.data), "_blank");
      if (print && w) w.onload = () => w.print();
    } catch { toast.error("Could not open statement PDF"); }
  };
  const stmtSendEmail = async () => {
    if (!stmtEmail) { toast.error("Enter a recipient email"); return; }
    setStmtSending(true);
    try {
      await api.post(`/admin/clients/${active.id}/statement/send-email`, { recipient: stmtEmail, ...stmtParams() });
      toast.success("Statement emailed with PDF attached");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Email failed"); }
    finally { setStmtSending(false); }
  };
  const stmtWhatsApp = () => {
    const num = (active.whatsapp || "").replace(/[^0-9]/g, "");
    const t = encodeURIComponent(`Hello ${active.name || ""}, please find your ApexPath statement of account${stmtData ? ` (${stmtData.period.label}) — balance outstanding £${Number(stmtData.totals.outstanding).toFixed(2)}` : ""}. I'll send the PDF across now.`);
    window.open(`https://wa.me/${num}?text=${t}`, "_blank");
  };

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (q) params.q = q;
    api.get("/admin/clients", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [status, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const open = async (c) => {
    const { data } = await api.get(`/admin/clients/${c.id}`);
    setActive(data); setDraft({ ...data });
  };
  const saveField = async (patch) => {
    const { data } = await api.put(`/admin/clients/${active.id}`, patch);
    setActive((a) => ({ ...data, matters: a.matters })); setDraft({ ...data, matters: active.matters }); load();
  };
  const saveAll = async () => {
    try {
      const { first_name, last_name, email, phone, nationality, dob, address, city, postcode, country, status, assigned_to, comm_preference, whatsapp } = draft;
      await saveField({ first_name, last_name, email, phone, nationality, dob, address, city, postcode, country, status, assigned_to, comm_preference, whatsapp });
      toast.success("Client saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addNote = async () => { if (!note.trim()) return; await saveField({ note }); setNote(""); toast.success("Note added"); };
  const addContact = async () => {
    if (!contact.name.trim()) return;
    const { data } = await api.post(`/admin/clients/${active.id}/contacts`, contact);
    setActive((a) => ({ ...data, matters: a.matters })); setContact({ name: "", relationship: "", email: "", phone: "" });
  };
  const removeContact = async (cid) => {
    const { data } = await api.delete(`/admin/clients/${active.id}/contacts/${cid}`);
    setActive((a) => ({ ...data, matters: a.matters }));
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/clients", form);
      if (data.duplicates?.length) toast.warning(`${data.duplicates.length} possible duplicate client(s) with this email/phone.`);
      else toast.success("Client created");
      setCreating(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const d = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const exportCsv = () => {
    const rows = [["Name", "Email", "Phone", "Nationality", "Status", "Assigned"]];
    items.forEach((c) => rows.push([c.name, c.email, c.phone || "", c.nationality || "", c.status, c.assigned_to || ""]));
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "clients.csv"; a.click();
  };

  return (
    <div data-testid="clients-manager">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-navy">Clients</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} client record(s) · the master profile reused across matters.</p>
        </div>
        <div className="flex gap-2">
          {can("clients", "create") && <button onClick={() => { setForm(EMPTY); setCreating(true); }} data-testid="new-client-btn" className="btn-gold text-sm"><Plus size={15} /> New client</button>}
          <button onClick={exportCsv} className="btn-navy text-sm"><Download size={15} /> Export CSV</button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, nationality…" className="pl-9" data-testid="client-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="client-status-filter">
          <option value="all">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3 hidden sm:table-cell">Nationality</th><th className="px-5 py-3 hidden lg:table-cell">Assigned</th><th className="px-5 py-3">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((c) => (
              <tr key={c.id} data-testid={`client-row-${c.id}`} onClick={() => open(c)} className="cursor-pointer hover:bg-slate-50">
                <td className="px-5 py-3"><div className="font-medium text-navy">{c.name}</div><div className="text-xs text-slate-400">{c.email}</div></td>
                <td className="px-5 py-3 hidden sm:table-cell text-slate-600">{c.nationality || "—"}</td>
                <td className="px-5 py-3 hidden lg:table-cell text-slate-500">{c.assigned_to || "—"}</td>
                <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${badge(c.status)}`}>{c.status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">No clients yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setActive(null)} />
          <div className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="font-serif text-xl font-semibold text-navy">{active.name}</h2>
              <button onClick={() => setActive(null)} className="text-slate-400 hover:text-navy"><X size={20} /></button>
            </div>
            <div className="flex-1 space-y-6 overflow-y-auto p-6">
              <fieldset disabled={!can("clients", "edit")} className="space-y-4 disabled:opacity-70">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">First name</Label><Input data-testid="client-first" value={draft.first_name || ""} onChange={(e) => d("first_name", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">Last name</Label><Input data-testid="client-last" value={draft.last_name || ""} onChange={(e) => d("last_name", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">Email</Label><Input data-testid="client-email" value={draft.email || ""} onChange={(e) => d("email", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">Phone</Label><Input value={draft.phone || ""} onChange={(e) => d("phone", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">Nationality</Label><Input value={draft.nationality || ""} onChange={(e) => d("nationality", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">Date of birth</Label><Input type="date" value={draft.dob || ""} onChange={(e) => d("dob", e.target.value)} className="mt-1" /></div>
                  <div className="col-span-2"><Label className="text-xs">Address</Label><Input value={draft.address || ""} onChange={(e) => d("address", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">City</Label><Input value={draft.city || ""} onChange={(e) => d("city", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">Postcode</Label><Input value={draft.postcode || ""} onChange={(e) => d("postcode", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">Status</Label><select data-testid="client-status" value={draft.status || "Prospect"} onChange={(e) => d("status", e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
                  <div><Label className="text-xs">Assigned adviser</Label><select data-testid="client-assign" value={draft.assigned_to || ""} onChange={(e) => d("assigned_to", e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"><option value="">Unassigned</option>{staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}</select></div>
                  <div><Label className="text-xs">Preferred contact</Label><select data-testid="client-comm-pref" value={draft.comm_preference || "Email"} onChange={(e) => d("comm_preference", e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{COMM_PREFS.map((s) => <option key={s}>{s}</option>)}</select></div>
                  <div><Label className="text-xs">WhatsApp number</Label><Input data-testid="client-whatsapp" value={draft.whatsapp || ""} onChange={(e) => d("whatsapp", e.target.value)} placeholder="+44…" className="mt-1" /></div>
                </div>
                <button onClick={saveAll} data-testid="save-client-btn" className="btn-gold w-full text-sm">Save client</button>
              </fieldset>

              {can("finance", "view") && (
                <button onClick={openStatement} data-testid="statement-btn" className="flex w-full items-center justify-center gap-2 rounded-lg border border-navy/20 bg-navy/5 py-2 text-sm font-medium text-navy hover:bg-navy/10">
                  <FileText size={15} /> Statement of Account
                </button>
              )}

              <div>
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-slate-400"><FolderOpen size={14} /> Matters ({active.matters?.length || 0})</div>
                {(active.matters || []).map((m) => (
                  <div key={m.id} className="mb-2 flex items-center justify-between rounded-lg border border-slate-100 p-2.5 text-xs">
                    <div><span className="font-medium text-navy">{m.ref}</span> · {m.matter_type}</div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{m.status}</span>
                  </div>
                ))}
                {(active.matters || []).length === 0 && <p className="text-xs text-slate-400">No matters yet. Open one from the Matters page.</p>}
              </div>

              <div>
                <div className="mb-2 text-xs font-semibold uppercase text-slate-400">Linked contacts</div>
                {(active.contacts || []).map((c) => (
                  <div key={c.id} className="mb-2 flex items-center justify-between rounded-lg border border-slate-100 p-2.5 text-xs">
                    <div><span className="font-medium text-navy">{c.name}</span>{c.relationship ? ` · ${c.relationship}` : ""}<div className="text-slate-400">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div></div>
                    {can("clients", "edit") && <button onClick={() => removeContact(c.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>}
                  </div>
                ))}
                {can("clients", "edit") && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Input data-testid="contact-name" placeholder="Name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                    <Input placeholder="Relationship" value={contact.relationship} onChange={(e) => setContact({ ...contact, relationship: e.target.value })} />
                    <Input placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                    <Input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                    <button onClick={addContact} data-testid="add-contact-btn" className="btn-navy col-span-2 text-xs"><UserPlus size={13} /> Add contact</button>
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2 text-xs font-semibold uppercase text-slate-400">Notes</div>
                {(active.notes || []).map((n, i) => (
                  <div key={i} className="mb-2 rounded-lg border border-slate-100 bg-slate-50 p-2.5 text-xs"><div className="text-slate-700">{n.text}</div><div className="mt-1 text-slate-400">{n.by} · {fmt(n.at)}</div></div>
                ))}
                {can("clients", "edit") && <><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" rows={2} data-testid="client-note-input" />
                <button onClick={addNote} className="btn-navy mt-2 text-xs">Add note</button></>}
              </div>
            </div>
          </div>
        </div>
      )}

      {stmtOpen && active && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setStmtOpen(false)} />
          <div className="relative z-10 max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" data-testid="statement-modal">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-serif text-xl font-semibold text-navy">Statement of Account</h2><button onClick={() => setStmtOpen(false)} className="text-slate-400"><X size={18} /></button></div>
            <p className="mb-3 text-sm text-slate-600">{active.name} — invoices, payments, credits and outstanding balance for the chosen period.</p>

            <div className="mb-3 grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Financial year</Label>
                <select value={stmt.fy} onChange={(e) => setStmt({ ...stmt, fy: e.target.value, date_from: "", date_to: "" })} data-testid="statement-fy" className="mt-1 w-full rounded-md border border-slate-300 px-2 py-2 text-sm">
                  <option value="all">All time</option>
                  {Array.from({ length: 6 }, (_, i) => curFy - i).map((y) => <option key={y} value={y}>{`FY ${y}/${String(y + 1).slice(-2)}`}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">From (custom)</Label><Input type="date" value={stmt.date_from} onChange={(e) => setStmt({ ...stmt, date_from: e.target.value })} data-testid="statement-from" className="mt-1" /></div>
              <div><Label className="text-xs">To (custom)</Label><Input type="date" value={stmt.date_to} onChange={(e) => setStmt({ ...stmt, date_to: e.target.value })} data-testid="statement-to" className="mt-1" /></div>
            </div>
            <p className="mb-3 text-[11px] text-slate-400">A custom date range overrides the financial year.</p>

            {stmtData && (
              <div className="mb-4 rounded-lg border border-slate-200" data-testid="statement-preview">
                <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">{stmtData.period.label}</div>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="text-[10px] uppercase text-slate-400"><tr><th className="px-3 py-1.5">Date</th><th className="px-3 py-1.5">Invoice</th><th className="px-3 py-1.5">Status</th><th className="px-3 py-1.5 text-right">Balance</th></tr></thead>
                    <tbody>
                      {stmtData.rows.map((r) => <tr key={r.id} className="border-t border-slate-50"><td className="px-3 py-1.5">{r.date}</td><td className="px-3 py-1.5 font-medium text-navy">{r.ref}</td><td className="px-3 py-1.5">{r.status}</td><td className="px-3 py-1.5 text-right">£{Number(r.balance).toFixed(2)}</td></tr>)}
                      {stmtData.rows.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">No invoices in this period.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="grid grid-cols-4 gap-2 border-t border-slate-100 p-3 text-center text-xs">
                  <div><div className="text-slate-400">Invoiced</div><div className="font-semibold text-navy">£{Number(stmtData.totals.invoiced).toFixed(2)}</div></div>
                  <div><div className="text-slate-400">Paid</div><div className="font-semibold text-emerald-600">£{Number(stmtData.totals.paid).toFixed(2)}</div></div>
                  <div><div className="text-slate-400">Credited</div><div className="font-semibold text-purple-600">£{Number(stmtData.totals.credited).toFixed(2)}</div></div>
                  <div><div className="text-slate-400">Outstanding</div><div className="font-semibold text-amber-600" data-testid="statement-outstanding">£{Number(stmtData.totals.outstanding).toFixed(2)}</div></div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button onClick={() => stmtPdf(false)} data-testid="statement-preview-btn" className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-navy hover:bg-slate-50"><FileText size={14} /> Preview / Download</button>
              <button onClick={() => stmtPdf(true)} data-testid="statement-print-btn" className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-navy hover:bg-slate-50"><Printer size={14} /> Print</button>
              {active.whatsapp && <button onClick={stmtWhatsApp} data-testid="statement-whatsapp-btn" className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-medium text-white"><MessageCircle size={14} /> WhatsApp</button>}
            </div>

            {can("finance", "edit") && (
              <div className="mt-3 space-y-2 rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                <div className="text-xs font-semibold uppercase text-blue-700">Email the statement (PDF attached)</div>
                <Input value={stmtEmail} onChange={(e) => setStmtEmail(e.target.value)} data-testid="statement-email-recipient" placeholder="client@email.com" />
                <button onClick={stmtSendEmail} disabled={stmtSending} data-testid="statement-send-email-btn" className="btn-gold flex w-full items-center justify-center gap-1 disabled:opacity-60"><Mail size={14} /> {stmtSending ? "Sending…" : "Send statement by email"}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCreating(false)} />
          <div className="relative z-10 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-serif text-xl font-semibold text-navy">New client</h2><button onClick={() => setCreating(false)} className="text-slate-400"><X size={18} /></button></div>
            <form onSubmit={submitCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First name</Label><Input data-testid="create-client-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required className="mt-1" /></div>
                <div><Label>Last name</Label><Input data-testid="create-client-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input data-testid="create-client-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nationality</Label><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} className="mt-1" /></div>
                <div><Label>Status</Label><select data-testid="create-client-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Preferred contact</Label><select data-testid="create-client-comm" value={form.comm_preference} onChange={(e) => setForm({ ...form, comm_preference: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{COMM_PREFS.map((s) => <option key={s}>{s}</option>)}</select></div>
                <div><Label>WhatsApp number</Label><Input data-testid="create-client-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+44…" className="mt-1" /></div>
              </div>
              <button type="submit" data-testid="create-client-submit" className="btn-gold w-full">Create client</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
